import { useCallback } from 'react';
import { Stack, Box, SxProps } from '@mui/material';

import { CircularProgress, Divider, Paper, Typography } from '@occc-app/design-system';
import { DEFAULT_ERROR_MESSAGE } from '@occc-app/lib/constants';

export type PageSectionProps = {
  title: string;
  description?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
  isLoading?: boolean;
  hasError?: boolean;
  contentSx?: SxProps;
};

export const PageSection = ({
  title,
  description,
  children,
  actions,
  isLoading = false,
  hasError = false,
  contentSx,
}: PageSectionProps) => {
  const renderContent = useCallback(() => {
    if (isLoading) {
      return (
        <Box display='flex' justifyContent='center' alignItems='center'>
          <CircularProgress />
        </Box>
      );
    }

    if (hasError) {
      return (
        <Typography color='error' align='center'>
          {DEFAULT_ERROR_MESSAGE}
        </Typography>
      );
    }

    return children;
  }, [isLoading, hasError, children]);

  return (
    <Paper
      elevation={2}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 1,
        mb: 1,
      }}
    >
      <Stack direction='row' justifyContent='space-between' alignItems='center' p={3} py={2}>
        <Box>
          <Typography variant='subtitle1' fontWeight={600}>
            {title}
          </Typography>
          {description && (
            <Typography variant='body2' color='gray'>
              {description}
            </Typography>
          )}
        </Box>
        {actions && <Box>{actions}</Box>}
      </Stack>

      <Divider />

      <Stack minHeight={100} justifyContent='center' sx={contentSx}>
        {renderContent()}
      </Stack>
    </Paper>
  );
};
